/**
 * SweepModal — confirm + run the idempotent expiry sweep (POST /inventory/sweep, cap
 * inventory.mouvement.create). Transitions every expired actif lot → perime and posts a qty-0
 * `peremption` ledger row. Idempotent + audited; re-running is safe.
 */
import { Button, Modal, useToast } from "@/ui";
import { useCan } from "@/auth";
import { useT } from "@/app";
import { ApiError } from "@/api";
import { INV_CAPS } from "../caps";
import { useLabId, useSweep } from "../api";

export interface SweepModalProps {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}

export function SweepModal({ open, onClose, onDone }: SweepModalProps) {
  const { t } = useT();
  const toast = useToast();
  const labId = useLabId();
  const canSweep = useCan(INV_CAPS.mouvementCreate);
  const mutation = useSweep(labId);

  const handleClose = () => {
    if (mutation.isPending) return;
    onClose();
  };

  const run = () => {
    if (!canSweep) return;
    mutation.mutate(
      {},
      {
        onSuccess: (res) => {
          const n = res.swept?.length ?? 0;
          toast.success(
            n === 0 ? t("inventory.sweep.success.none") : t("inventory.sweep.success.some", { n }),
          );
          onDone();
        },
        onError: (err) => toast.error(err instanceof ApiError ? err.message : t("inventory.field.required")),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("inventory.sweep.confirm.title")}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={mutation.isPending}>
            {t("inventory.action.cancel")}
          </Button>
          <Button variant="danger" loading={mutation.isPending} disabled={!canSweep} onClick={run}>
            {t("inventory.sweep.submit")}
          </Button>
        </>
      }
    >
      <p style={{ margin: 0, color: "var(--nv-slate)" }}>{t("inventory.sweep.confirm.warn")}</p>
    </Modal>
  );
}
